import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import type { ApiResponse } from '../types/billValidationTypes';
import type { BvBill } from '../types/billTypes';
import { monthEnd } from '../lib/billMonths';
import { assignedMonths, type MonthMode } from '../lib/billRunRows';
import { filterRunSites, isBvSite, matchesRunSiteFilters, type BillRunSiteFilters } from '../lib/billRunConditions';
import { MAX_NMI_MONTHS, type ExpectedSite, type MissingTagRow } from '../lib/missingBills';
import { foldSendHistory, type SendHistory, type SendRowLike } from '../lib/reportStatus';
import type { InvoiceEmailLike } from '../utils/billParseStatus';
import {
  bulkReportService, type PipelineAnalysisRow, type PipelineData, type PipelineQueueRow,
} from './bulkReportService';
import { missingBillService } from './missingBillService';

// Bill Runs (PRD 2026-10-21, US-002): everything the page needs for the months
// it asked for, and nothing else. Straight Supabase reads through the browser
// client, so RLS scopes every row to the viewer's organisation.

const PAGE_SIZE = 1000;
/**
 * Bills come back whole (`*`: parsed invoice and results JSON) with their site
 * and client. 1000 of those in one request ran past the 8 s statement timeout
 * on prod while a bulk send was running; 250 keeps each request short.
 */
const BILL_PAGE_SIZE = 250;
/** Ids per `.in()` — keeps each request URL well under PostgREST's limit. */
const IDS_PER_CALL = 200;
const MONTH_KEY = /^\d{4}-(0[1-9]|1[0-2])$/;

/** billService.listAll's columns plus client_id (the Entity link), so a bill renders identically. `!inner` when a site filter narrows by the join. */
const siteSelect = (inner: boolean, clientInner: boolean) =>
  `*, site:site_id${inner ? '!inner' : ''}(nmi, client_id, bv_extn, addr_street, addr_city, addr_state, addr_postcode, current_retailer, drive_link, client:client_id${clientInner ? '!inner' : ''}(name, drive_link))`;
const SEND_COLUMNS = 'id, bill_id, status, sent_at, created_at, error, expected_total, invoice_total';
const EMAIL_COLUMNS = 'id, bill_id, assigned_at, attachments:bv_invoice_email_attachment(parsing_status, parse_error, updated_at)';
const ANALYSIS_COLUMNS = 'id, bill_id, site_id, period_from, period_to, data_status, data_error, tariff_status, tariff_error, tou_status, tou_error';

export type BillRunsFetchInput = {
  /** `YYYY-MM`, 1 to 24 (MAX_NMI_MONTHS). */
  months: string[];
  mode: MonthMode;
  siteFilters: BillRunSiteFilters;
  includeArchived: boolean;
};

export type BillRunsData = PipelineData & {
  /** The selected months, sorted and de-duplicated. */
  months: string[];
  mode: MonthMode;
  /** Expected sites that pass the site filters. */
  sites: ExpectedSite[];
  /** Active missing-month tags for the months. */
  tags: MissingTagRow[];
  /** Site id → latest active bill_to, over all time ("Last bill", "Never billed"). */
  lastBillTo: Map<string, string>;
  /** Set when send history could not be read: send statuses then read as never sent. */
  sendHistoryError: string | null;
  /**
   * Reads that failed and were left empty, named for the user ("invoice emails: <error>").
   * Bills and sites are required; the rest degrade so one slow read never blanks the page.
   */
  partial: string[];
};

function fail<T>(error: unknown): ApiResponse<T> {
  return { success: false, error: error instanceof Error ? error.message : String((error as { message?: string })?.message ?? error) };
}

/** `read`, or `fallback` when it fails, with the failure noted in `partial`. */
async function soft<T>(partial: string[], label: string, read: Promise<T>, fallback: T): Promise<T> {
  try {
    return await read;
  } catch (e: unknown) {
    partial.push(`${label}: ${fail(e).error}`);
    return fallback;
  }
}

async function currentOrgId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('base_organisation_members')
    .select('organisation_id')
    .eq('user_id', user.id)
    .limit(1)
    .maybeSingle();
  return (data?.organisation_id as string | undefined) ?? null;
}

type Page<T> = PromiseLike<{ data: T[] | null; error: unknown }>;

/** Postgres' "canceling statement due to statement timeout". */
const STATEMENT_TIMEOUT = '57014';
const TIMEOUT_RETRY_MS = 1_500;

/**
 * Every row of a paged read — an unbounded select stops at 1000 silently.
 * A page cancelled by the role's statement timeout (8 s) is tried once more:
 * that timeout is load (a bulk send's workers, the tick's parses), not the
 * query, and the next try usually fits.
 */
export async function paged<T>(
  page: (from: number, to: number) => Page<T>,
  size: number = PAGE_SIZE,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
): Promise<T[]> {
  const all: T[] = [];
  for (let p = 0; ; p++) {
    const read = () => page(p * size, (p + 1) * size - 1);
    let { data, error } = await read();
    if ((error as { code?: string } | null)?.code === STATEMENT_TIMEOUT) {
      await sleep(TIMEOUT_RETRY_MS);
      ({ data, error } = await read());
    }
    if (error) throw error;
    all.push(...(data ?? []));
    if (!data || data.length < size) break;
  }
  return all;
}

/** One read per chunk of ids, rows concatenated. */
async function byIds<T>(ids: string[], read: (chunk: string[]) => Promise<T[]>): Promise<T[]> {
  const out: T[] = [];
  for (let i = 0; i < ids.length; i += IDS_PER_CALL) out.push(...(await read(ids.slice(i, i + IDS_PER_CALL))));
  return out;
}

/**
 * The bills whose period touches the months' span, scoped and filtered in the
 * query. Both modes read the same span: in Period end mode a bill ending after
 * the last month can still cover an earlier one ("Covered by"), so it is read,
 * and the rows assign it by bill_to (lib/billRunRows).
 */
async function loadBills(orgId: string, first: string, last: string, input: BillRunsFetchInput): Promise<BvBill[]> {
  const f = input.siteFilters;
  const inner = !!(f.entity || f.retailer.length || f.state || f.distributor || f.tariff);
  return paged<BvBill>((from, to) => {
    let q = supabase
      .from('bv_bill')
      .select(siteSelect(inner, !!f.entity))
      .eq('org_id', orgId)
      .lte('bill_from', monthEnd(last))
      .gte('bill_to', `${first}-01`);
    if (!input.includeArchived) q = q.is('archived_at', null);
    // Site filters on the joined site (`!inner` above). Exact matches; the
    // trimmed comparison after the fetch (matchesRunSiteFilters) is the rule.
    if (f.state) q = q.eq('site.addr_state', f.state);
    if (f.retailer.length) q = q.in('site.current_retailer', f.retailer);
    if (f.distributor) q = q.eq('site.bv_extn->>network_distributor', f.distributor);
    if (f.tariff) q = q.eq('site.bv_extn->>network_tariff_code', f.tariff);
    if (f.entity) q = q.eq('site.client.name', f.entity);
    return q.order('bill_from', { ascending: true }).order('id', { ascending: true }).range(from, to) as unknown as Page<BvBill>;
  }, BILL_PAGE_SIZE);
}

/**
 * Each site's latest active bill end date, over all time: two small columns of
 * every active bill in the org (indexed on org_id), read alongside the month's
 * bills. ponytail: ~4k bills today (4 pages); move to a grouped RPC
 * (max(bill_to) per site) if bv_bill grows past ~50k rows.
 */
async function loadLastBills(orgId: string): Promise<Map<string, string>> {
  const rows = await paged<{ site_id: string; bill_to: string | null }>((from, to) =>
    supabase.from('bv_bill').select('site_id, bill_to')
      .eq('org_id', orgId).is('archived_at', null).not('bill_to', 'is', null)
      .order('id', { ascending: true }).range(from, to) as unknown as Page<{ site_id: string; bill_to: string | null }>);
  const last = new Map<string, string>();
  for (const r of rows) {
    if (r.bill_to && r.bill_to > (last.get(r.site_id) ?? '')) last.set(r.site_id, r.bill_to);
  }
  return last;
}

/**
 * Usage-data coverage for the given bills (migration 123).
 * ponytail: bv_board_coverage() takes no arguments, so it is still computed and
 * paged over every unpriced bill in the org and filtered here; give it an
 * optional bill-id or date-range parameter if this read becomes the slow one.
 */
async function coverageFor(billIds: ReadonlySet<string>): Promise<PipelineQueueRow[]> {
  if (billIds.size === 0) return [];
  const all = await paged<PipelineQueueRow>((from, to) =>
    supabase.rpc('bv_board_coverage').order('bill_id', { ascending: true }).range(from, to) as unknown as Page<PipelineQueueRow>);
  return all.filter((r) => billIds.has(r.bill_id));
}

export const billRunsService = {
  /**
   * Bills, expected sites, missing-month tags, and the pipeline and send
   * inputs for the selected months only (US-002).
   */
  fetch: async (input: BillRunsFetchInput): Promise<ApiResponse<BillRunsData>> => {
    try {
      const months = [...new Set(input.months)].sort();
      if (months.length === 0) return { success: false, error: 'Select at least one month' };
      if (months.length > MAX_NMI_MONTHS) return { success: false, error: `Select at most ${MAX_NMI_MONTHS} months` };
      if (!months.every((m) => MONTH_KEY.test(m))) return { success: false, error: 'Months must be YYYY-MM' };
      const first = months[0];
      const last = months[months.length - 1];

      const orgId = await currentOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };

      const partial: string[] = [];
      const [fetched, sitesRes, tagsRes, lastBillTo] = await Promise.all([
        loadBills(orgId, first, last, input),
        // ponytail: loadExpectedSites reads every ever-enrolled site in the org;
        // the site filters are applied after it (filterRunSites).
        missingBillService.loadExpectedSites(),
        missingBillService.loadActiveTags(first, last),
        soft(partial, 'last bill dates', loadLastBills(orgId), new Map<string, string>()),
      ]);
      if (!sitesRes.success) throw new Error(sitesRes.error ?? 'Could not load sites');
      if (!tagsRes.success) partial.push(`missing-bill tags: ${tagsRes.error ?? 'could not load'}`);

      // Search (NMI or entity) and the trimmed site-filter rule, after the query.
      // Tender sites (empty bv_extn) are not Bill Validation sites: their bills
      // stay out of every row, count and export.
      const bills = fetched.filter((b) => isBvSite(b.site) && matchesRunSiteFilters(b, input.siteFilters));
      const sites = filterRunSites(sitesRes.data ?? [], input.siteFilters);
      const siteIds = new Set(sites.map((s) => s.id));
      const tags = (tagsRes.data ?? []).filter((t) => siteIds.has(t.site_id));

      // Pipeline and send inputs only for the bills that make rows; the rest
      // were read to cover a month (Period end mode) and need dates only.
      const shown = bills.filter((b) => assignedMonths(b, input.mode, months).length > 0);
      const ids = shown.map((b) => b.id);
      const idSet = new Set(ids);

      const [emails, queue, analyses, sends, runRes] = await Promise.all([
        soft(partial, 'invoice emails', byIds(ids, async (chunk) => {
          const { data, error } = await supabase.from('bv_invoice_email').select(EMAIL_COLUMNS).in('bill_id', chunk);
          if (error) throw error;
          return (data ?? []) as unknown as InvoiceEmailLike[];
        }), []),
        soft(partial, 'usage data coverage', coverageFor(idSet), []),
        // By date, not id: analysisFinder also matches an analysis by site and
        // exact period when the run did not record its bill.
        ids.length === 0 ? Promise.resolve([] as PipelineAnalysisRow[]) : soft(partial, 'bill run results', paged<PipelineAnalysisRow>((from, to) =>
          supabase.from('bv_nem_analysis').select(ANALYSIS_COLUMNS)
            .eq('org_id', orgId)
            .lte('period_from', monthEnd(last))
            .gte('period_to', `${first}-01`)
            .order('id', { ascending: true })
            .range(from, to) as unknown as Page<PipelineAnalysisRow>), []),
        byIds(ids, async (chunk) => {
          const { data, error } = await supabase.from('bv_report_send').select(SEND_COLUMNS).in('bill_id', chunk);
          if (error) throw error;
          return (data ?? []) as SendRowLike[];
        }).then(
          (rows) => ({ history: foldSendHistory(rows), error: null as string | null }),
          (e: unknown) => ({ history: {} as Record<string, SendHistory>, error: fail(e).error ?? 'Could not load send history' }),
        ),
        bulkReportService.listRuns(1),
      ]);

      return {
        success: true,
        data: {
          months,
          mode: input.mode,
          bills,
          emails,
          queue,
          analyses: analyses.filter((a) => (a.bill_id ? idSet.has(a.bill_id) : true)),
          sendHistory: sends.history,
          sendHistoryError: sends.error,
          partial,
          lastRun: runRes.success ? runRes.data?.[0] ?? null : null,
          sites,
          tags,
          lastBillTo,
        },
      };
    } catch (error: unknown) {
      return fail(error);
    }
  },
};
